import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { CursorGlow } from '@/components/interactive/CursorGlow';
import { Footer } from '@/components/nav/Footer';
import { Header } from '@/components/nav/Header';
// Eagerly imported: home, persistent shell, the personalization trunk (the
// enterprise audience's entry path through the site).
import { About } from '@/pages/About';
import { Home } from '@/pages/Home';
import { NotFound } from '@/pages/NotFound';
import { Contact } from '@/pages/personalization/Contact';
import { Personalization } from '@/pages/personalization/Personalization';

// Lazy-loaded: the OSS-trunk pages and the docs route. These pull in heavier
// dependencies (shiki for the comparison block, react-markdown + remark-gfm
// for /docs/*, the platform content modules) and aren't on the enterprise
// hot-path, so we keep them out of the initial bundle.
const DocArticle = lazy(() =>
  import('@/pages/docs/DocArticle').then((m) => ({ default: m.DocArticle })),
);
const OrchestrationLanding = lazy(() =>
  import('@/pages/orchestration/OrchestrationLanding').then((m) => ({
    default: m.OrchestrationLanding,
  })),
);
const PlatformCommunityPage = lazy(() =>
  import('@/pages/orchestration/PlatformCommunityPage').then((m) => ({
    default: m.PlatformCommunityPage,
  })),
);
const PlatformExamplesPage = lazy(() =>
  import('@/pages/orchestration/PlatformExamplesPage').then((m) => ({
    default: m.PlatformExamplesPage,
  })),
);
const PlatformFeaturesPage = lazy(() =>
  import('@/pages/orchestration/PlatformFeaturesPage').then((m) => ({
    default: m.PlatformFeaturesPage,
  })),
);
const PlatformUseCasesPage = lazy(() =>
  import('@/pages/orchestration/PlatformUseCasesPage').then((m) => ({
    default: m.PlatformUseCasesPage,
  })),
);
const Customers = lazy(() =>
  import('@/pages/personalization/Customers').then((m) => ({
    default: m.Customers,
  })),
);
const Editorial = lazy(() =>
  import('@/pages/personalization/customers/Editorial').then((m) => ({
    default: m.Editorial,
  })),
);
const Financial = lazy(() =>
  import('@/pages/personalization/customers/Financial').then((m) => ({
    default: m.Financial,
  })),
);
const Privacy = lazy(() =>
  import('@/pages/legal/Privacy').then((m) => ({ default: m.Privacy })),
);
const Terms = lazy(() =>
  import('@/pages/legal/Terms').then((m) => ({ default: m.Terms })),
);

function RouteFallback() {
  // Minimal placeholder during chunk fetch — sized to avoid layout shift.
  // The cursor glow + cement texture provide enough visual continuity that
  // a spinner would be over-the-top for sub-second loads.
  return (
    <div
      aria-busy="true"
      style={{ minHeight: '60vh' }}
    />
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
    // pathname is intentionally listed in deps so we scroll to top on SPA navigations.
    void pathname;
  }, [pathname, hash]);
  return null;
}

function AppShell() {
  return (
    <>
      <a
        href="#main-content"
        style={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 9999,
          padding: '8px 16px',
          background: 'var(--accent)',
          color: 'var(--ink-on-accent)',
          borderRadius: 4,
          fontSize: 14,
          fontWeight: 600,
          transform: 'translateY(-200%)',
          transition: 'transform 0.2s',
        }}
        onFocus={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        onBlur={(e) => (e.currentTarget.style.transform = 'translateY(-200%)')}
      >
        Skip to main content
      </a>
      <CursorGlow />
      <ScrollToTop />
      <Header />
      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />

          {/* PRD-07 IA — personalization trunk */}
          <Route path="/personalization" element={<Personalization />} />
          <Route
            path="/personalization/customers"
            element={<Customers />}
          />
          <Route
            path="/personalization/customers/financial"
            element={<Financial />}
          />
          <Route
            path="/personalization/customers/editorial"
            element={<Editorial />}
          />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/personalization/contact"
            element={<Navigate to="/contact" replace />}
          />

          {/* Legacy paths — 301 to the personalization trunk */}
          <Route
            path="/product"
            element={<Navigate to="/personalization" replace />}
          />
          <Route
            path="/customers"
            element={<Navigate to="/personalization/customers" replace />}
          />
          <Route
            path="/customers/financial"
            element={
              <Navigate to="/personalization/customers/financial" replace />
            }
          />
          <Route
            path="/customers/editorial"
            element={
              <Navigate to="/personalization/customers/editorial" replace />
            }
          />

          <Route path="/about" element={<About />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/orchestration" element={<OrchestrationLanding />} />
          <Route path="/features" element={<PlatformFeaturesPage />} />
          <Route path="/use-cases" element={<PlatformUseCasesPage />} />
          <Route path="/examples" element={<PlatformExamplesPage />} />
          <Route path="/community" element={<PlatformCommunityPage />} />
          <Route
            path="/docs"
            element={<Navigate to="/docs/introduction" replace />}
          />
            <Route path="/docs/*" element={<DocArticle />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
